// 허들(슬랙 huddles) — 채널 머리글의 헤드폰 단추. 사이드바 아래의 도크는 `huddle_dock.tsx`
// 다 — 허들에 들어갔을 때만 뜨니 첫 화면에 싣지 않는다.
//
// 소리를 모른다. 들어가기·나오기·음소거는 손(`HuddleHands`)이 하고, 여기는 명단과 상태를
// 그리기만 한다. 사이드바 줄의 헤드폰은 `ChannelRow` 가 그린다.

import type { ChannelId, UserId } from '@maru/protocol'
import { Headphones } from 'lucide-react'
import { translate, useT } from './i18n.ts'
import { Button, LEAD_SIZE } from './kit/button.tsx'

/** 켜 둔 자막. 저장되지 않는다 — 허들을 나오면 사라진다. */
export interface HuddleCaptions {
  /** 지금 보일 줄들(오래된 것부터). */
  lines: readonly { who: UserId; text: string }[]
  /** 이 기기가 내 말도 받아 적는가. 기기 안 음성 인식이 없으면 남의 자막만 본다. */
  self: boolean
}

/** 영상 타일 하나 — 누군가의 카메라나 화면 공유. 트랙은 소리 층이 붙인 그대로다. */
export interface HuddleTile {
  /** 화면이 열쇠로 쓰는 고유 이름(트랙 sid). */
  key: string
  who: UserId
  source: 'camera' | 'screen'
  /** 내 기기에서 나가는 것인가. 내 카메라는 거울처럼 뒤집어 보인다. */
  self: boolean
  track: MediaStreamTrack
}

/** 내가 있는 허들. */
export interface HuddleMine {
  channel: ChannelId
  muted: boolean
  /** SFU 에 붙는 중이다. 소리가 아직 없다. */
  connecting: boolean
  /** 켜 두었으면 있다. */
  captions?: HuddleCaptions
  /** 내 카메라가 켜져 있으면 참. */
  camera?: boolean
  /** 내 화면을 공유하고 있으면 참. */
  screen?: boolean
  /** 보이는 영상들(카메라·화면 공유). 없으면 소리만 있는 허들이다. */
  tiles?: readonly HuddleTile[]
}

export interface HuddleHands {
  /** 내가 든 채널들의 열린 허들: 채널 → 사람들(들어온 순서). */
  live: ReadonlyMap<ChannelId, readonly UserId[]>
  mine: HuddleMine | null
  /** 사람에게 보일 마지막 실패(마이크 거부·가득 참 등). 없으면 `null`. */
  error: string | null
  join(channel: ChannelId): void
  leave(): void
  mute(on: boolean): void
  /** 라이브 자막을 켜고 끈다. */
  captions?(on: boolean): void
  /** 카메라를 켜고 끈다. */
  camera?(on: boolean): void
  /** 화면 공유를 시작·중지한다. 이 기기가 화면 공유를 못 하면 없다. */
  screen?(on: boolean): void
  clear_error(): void
}

/**
 * 머리글 단추의 글자. 안에 있으면 나가기, 누가 있으면 참여, 아무도 없으면 시작. 헤드폰은 아이콘이 그린다.
 * `t` 는 화면이 넘긴다 — 없으면 원문이다.
 */
export function huddle_label(
  hands: HuddleHands,
  channel: ChannelId,
  t: (ko: string, vars?: Readonly<Record<string, number>>) => string = (ko, vars) =>
    translate('ko', ko, vars),
): string {
  if (hands.mine?.channel === channel) return t('허들 나가기')
  const n = hands.live.get(channel)?.length ?? 0
  return n > 0 ? t('허들 참여 · {n}명', { n }) : t('허들 시작')
}

export function HuddleButton({ hands, channel }: { hands: HuddleHands; channel: ChannelId }) {
  const t = useT()
  const inside = hands.mine?.channel === channel
  const live = (hands.live.get(channel)?.length ?? 0) > 0
  return (
    <Button
      size="sm"
      variant={inside ? 'secondary' : 'ghost'}
      className="huddle-tool"
      data-live={(live && !inside) || undefined}
      icon={<Headphones size={LEAD_SIZE.sm} aria-hidden />}
      pressed={inside}
      onClick={() => (inside ? hands.leave() : hands.join(channel))}
    >
      {huddle_label(hands, channel, t)}
    </Button>
  )
}
